// App chrome: grouped sidebar nav on the left, page content on the right.
// Collapses to an icon rail (remembered per machine) and becomes a drawer
// with a slim top bar on narrow screens. The login page renders bare.
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getToken, clearToken } from '../lib/auth';
import './AppShell.css';

const LS_COLLAPSED = 'farmos.sidebarCollapsed';

// 24px line icons, drawn for this app (stroke = currentColor).
const ICONS = {
  farm: <><path d="M12 21v-9" /><path d="M12 12c0-4.4 3-7.5 8-7.5 0 5-3 7.5-8 7.5Z" /><path d="M12 14.5C12 11 9.6 8.5 4.5 8.5c0 4 2.6 6 7.5 6Z" /></>,
  trays: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.2" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.2" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.2" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.2" /></>,
  harvests: <><path d="M3.5 10.5h17l-1.7 8.1a2 2 0 0 1-2 1.6H7.2a2 2 0 0 1-2-1.6Z" /><path d="m8 10.5 3-6.5" /><path d="m16 10.5-3-6.5" /><path d="M9 14.5v2.5M15 14.5v2.5M12 14.5v2.5" /></>,
  tasks: <><rect x="4" y="4" width="16" height="16" rx="2.5" /><path d="m8.5 12.3 2.4 2.4 4.6-5" /></>,
  sales: <><path d="M6 3.5h12v17l-3-1.8-3 1.8-3-1.8-3 1.8Z" /><path d="M9 8.5h6M9 12h6M9 15.5h3.5" /></>,
  subscriptions: <><path d="M4.5 11.5a7.5 7.5 0 0 1 13-4.6l1.8 1.8" /><path d="M19.5 4.5v4.2h-4.2" /><path d="M19.5 12.5a7.5 7.5 0 0 1-13 4.6l-1.8-1.8" /><path d="M4.5 19.5v-4.2h4.2" /></>,
  catalogue: <><path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.5 1.5 0 0 1 0 2.1l-6.4 6.4a1.5 1.5 0 0 1-2.1 0Z" /><circle cx="8" cy="8" r="1.6" /></>,
  marketing: <><path d="M3.5 10.2v3.6a1 1 0 0 0 1 1h2.2l5.8 4.2V5L6.7 9.2H4.5a1 1 0 0 0-1 1Z" /><path d="M16 9.2a3.6 3.6 0 0 1 0 5.6" /><path d="M18.6 6.4a7.4 7.4 0 0 1 0 11.2" /></>,
  leaflets: <><path d="M9 4.5 3.5 6.8v12.7L9 17.2l6 2.3 5.5-2.3V4.5L15 6.8Z" /><path d="M9 4.5v12.7M15 6.8v12.7" /></>,
  reviews: <path d="m12 3.8 2.5 5.1 5.6.8-4 4 1 5.6-5.1-2.7-5.1 2.7 1-5.6-4-4 5.6-.8Z" />,
  insights: <><path d="M4 4v16h16" /><path d="M8 16v-4M12 16V8.5M16 16v-6" /></>,
  finance: <><path d="M15.6 6.6A3.6 3.6 0 0 0 9 8.6V19" /><path d="M6.5 12.8h6.5" /><path d="M6.5 19h11" /></>,
  briefings: <><path d="M3.5 18.5h17" /><path d="M7 18.5a5 5 0 0 1 10 0" /><path d="M12 6v3" /><path d="m5.6 9.6 1.8 1.8" /><path d="m18.4 9.6-1.8 1.8" /></>,
  ai: <><path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3" /><path d="m12 8.2 1.2 2.6 2.6 1.2-2.6 1.2L12 15.8l-1.2-2.6L8.2 12l2.6-1.2Z" /><path d="m6.2 6.2 1.4 1.4M16.4 16.4l1.4 1.4M17.8 6.2l-1.4 1.4M7.6 16.4l-1.4 1.4" /></>,
  settings: <><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2.2" /><circle cx="9" cy="17" r="2.2" /></>,
  logout: <><path d="M14 4h3.5A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5H14" /><path d="m9.5 16-4-4 4-4" /><path d="M5.5 12H15" /></>,
  collapse: <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
};

const Icon = ({ name }) => (
  <svg className="app-icon" viewBox="0 0 24 24" width="18" height="18" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

const NAV = [
  { section: 'Growing', items: [
    { to: '/',             label: 'Farm',     icon: 'farm' },
    { to: '/tray-manager', label: 'Trays',    icon: 'trays' },
    { to: '/harvests',     label: 'Harvests', icon: 'harvests' },
    { to: '/task-manager', label: 'Tasks',    icon: 'tasks' },
  ] },
  { section: 'Selling', items: [
    { to: '/orders',       label: 'Sales',         icon: 'sales' },
    { to: '/subscribers',  label: 'Subscriptions', icon: 'subscriptions' },
    { to: '/products',     label: 'Catalogue',     icon: 'catalogue' },
  ] },
  { section: 'Customers', items: [
    { to: '/email-studio', label: 'Marketing', icon: 'marketing' },
    { to: '/leaflets',     label: 'Leaflets',  icon: 'leaflets' },
    { to: '/reviews',      label: 'Reviews',   icon: 'reviews' },
    { to: '/analytics',    label: 'Insights',  icon: 'insights' },
  ] },
  { section: 'Business', items: [
    { to: '/briefings',    label: 'Briefings', icon: 'briefings' },
    { to: '/ai-activity',  label: 'AI activity', icon: 'ai' },
    { to: '/accounting',   label: 'Finance', icon: 'finance' },
  ] },
];

const readCollapsed = () => {
  try { return localStorage.getItem(LS_COLLAPSED) === '1'; } catch { return false; }
};

export default function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (location.pathname === '/login' || !getToken()) return children;

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(LS_COLLAPSED, c ? '0' : '1'); } catch { /* private mode */ }
      return !c;
    });
  };

  const handleLogout = () => { clearToken(); navigate('/login', { replace: true }); };

  const navLink = ({ to, label, icon }) => {
    const active = location.pathname === to;
    return (
      <Link
        key={to}
        to={to}
        className={`app-nav-link${active ? ' is-active' : ''}`}
        aria-current={active ? 'page' : undefined}
        title={collapsed ? label : undefined}
      >
        <Icon name={icon} />
        <span className="app-nav-label">{label}</span>
      </Link>
    );
  };

  return (
    <div className={`app-shell${collapsed ? ' is-collapsed' : ''}${drawerOpen ? ' is-drawer-open' : ''}`}>
      <aside className="app-sidebar" aria-label="Main navigation">
        <Link to="/" className="app-brand" aria-label="TearDrop Farm OS home" title={collapsed ? 'TearDrop Farm OS' : undefined}>
          <img src="https://teardrophydro.co.uk/images/logo/icon.png" alt="" className="app-brand-logo" />
          <span className="app-brand-text">
            TearDrop<span className="app-brand-sub">Farm OS</span>
          </span>
        </Link>

        <nav className="app-nav">
          {NAV.map(({ section, items }) => (
            <div key={section} className="app-nav-group">
              <div className="app-nav-section">{section}</div>
              {items.map(navLink)}
            </div>
          ))}
        </nav>

        <div className="app-nav-footer">
          {navLink({ to: '/settings', label: 'Settings', icon: 'settings' })}
          <button type="button" className="app-nav-link app-nav-button" onClick={handleLogout} title={collapsed ? 'Log out' : undefined}>
            <Icon name="logout" />
            <span className="app-nav-label">Log out</span>
          </button>
          <button
            type="button"
            className="app-nav-link app-nav-button app-collapse"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : undefined}
          >
            <Icon name="collapse" />
            <span className="app-nav-label">Collapse</span>
          </button>
        </div>
      </aside>

      <div className="app-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />

      <div className="app-body">
        <header className="app-topbar">
          <button
            type="button"
            className="app-topbar-burger"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((o) => !o)}
          >
            <Icon name={drawerOpen ? 'close' : 'menu'} />
          </button>
          <Link to="/" className="app-brand app-brand--topbar" aria-label="TearDrop Farm OS home">
            <img src="https://teardrophydro.co.uk/images/logo/icon.png" alt="" className="app-brand-logo" />
            <span className="app-brand-text">TearDrop<span className="app-brand-sub">Farm OS</span></span>
          </Link>
        </header>
        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}
